import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  ShieldCheck, 
  Phone, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Scale,
  Wrench,
  Package,
  Info
} from "lucide-react";
import { SEO } from "@/components/SEO";

const BUSINESS_PHONE_LINK = "tel:+33549057490";

export default function GarantiePage() {
  useEffect(() => {}, []);

  return (
    <div className="min-h-screen" data-testid="garantie-page">
      <SEO
        title="Garantie Réparation 6 Mois - Mediastore Niort"
        description="Toutes les réparations Mediastore Niort sont garanties 6 mois (pièces et main d'œuvre). Garantie légale 2 ans sur le matériel neuf vendu. Pièces de qualité d'origine."
        keywords="garantie réparation Niort, garantie 6 mois Mediastore, garantie iPhone réparé, garantie pièces réparation téléphone, garantie légale matériel informatique Niort"
        path="/garantie"
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Garantie", path: "/garantie" },
        ]}
      />
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-blue-50 to-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full mb-6">
              <ShieldCheck className="h-5 w-5" />
              <span className="font-semibold">Garanties & Protections</span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Conditions de <span className="text-blue-600">Garantie</span>
            </h1>

            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Chez Mediastore, nous nous engageons à vous offrir des services de qualité 
              avec des garanties claires et transparentes. Découvrez nos conditions.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto space-y-12">

            {/* Garantie Réparations */}
            <Card className="border-2 border-blue-200">
              <CardHeader className="bg-blue-50">
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <div className="w-12 h-12 rounded-xl bg-blue-600 text-white flex items-center justify-center">
                    <Wrench className="h-6 w-6" />
                  </div>
                  Garantie sur les Réparations
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <p className="text-xl font-bold text-blue-700 flex items-center gap-2">
                    <Clock className="h-6 w-6" />
                    6 mois de garantie
                  </p>
                  <p className="text-slate-600 mt-1">sur les pièces remplacées et la main d&apos;œuvre</p>
                </div>

                <div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-3 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Ce qui est couvert
                  </h3>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                      Défaillance de la pièce remplacée dans des conditions normales d&apos;utilisation
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                      Vice de fabrication de la pièce installée
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                      Défaut de montage imputable à notre intervention
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                      Réparation ou remplacement gratuit en cas de défaut couvert
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-3 flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-red-600" />
                    Ce qui n&apos;est pas couvert (exclusions)
                  </h3>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start gap-2">
                      <XCircle className="h-4 w-4 text-red-500 mt-1 flex-shrink-0" />
                      Dommages causés par un choc, une chute, un écrasement ou une pression excessive
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="h-4 w-4 text-red-500 mt-1 flex-shrink-0" />
                      Dommages causés par l&apos;eau, l&apos;humidité ou tout liquide (oxydation)
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="h-4 w-4 text-red-500 mt-1 flex-shrink-0" />
                      Utilisation non conforme ou abusive de l&apos;appareil
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="h-4 w-4 text-red-500 mt-1 flex-shrink-0" />
                      Intervention par un tiers non autorisé après notre réparation
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="h-4 w-4 text-red-500 mt-1 flex-shrink-0" />
                      Dommages liés à un logiciel, virus ou mise à jour système
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="h-4 w-4 text-red-500 mt-1 flex-shrink-0" />
                      Usure normale des composants (batterie, boutons, etc.)
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="h-4 w-4 text-red-500 mt-1 flex-shrink-0" />
                      Pièces non fournies par Mediastore
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="h-4 w-4 text-red-500 mt-1 flex-shrink-0" />
                      Panne sur un composant différent de celui réparé
                    </li>
                  </ul>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="flex items-start gap-2 text-amber-800">
                    <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong>Important :</strong> La garantie réparation s&apos;applique uniquement à la pièce remplacée 
                      et à l&apos;intervention effectuée. Elle ne couvre pas les autres composants de l&apos;appareil 
                      ni les pannes préexistantes non signalées.
                    </span>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Garantie Légale - Vente de Matériel */}
            <Card className="border-2 border-emerald-200">
              <CardHeader className="bg-emerald-50">
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <div className="w-12 h-12 rounded-xl bg-emerald-600 text-white flex items-center justify-center">
                    <Package className="h-6 w-6" />
                  </div>
                  Garantie Légale - Vente de Matériel
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                  <p className="text-xl font-bold text-emerald-700 flex items-center gap-2">
                    <Scale className="h-6 w-6" />
                    Garantie légale de conformité : 2 ans
                  </p>
                  <p className="text-slate-600 mt-1">Conformément à la législation européenne (Directive 2019/771)</p>
                </div>

                <div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-3">
                    Vos droits en tant que consommateur
                  </h3>
                  <p className="text-slate-600 mb-4">
                    Pour tout achat de matériel informatique neuf auprès de Mediastore, vous bénéficiez 
                    automatiquement des garanties légales prévues par le Code de la consommation :
                  </p>

                  <div className="grid md:grid-cols-2 gap-4">
                    <Card className="bg-slate-50">
                      <CardContent className="p-4">
                        <h4 className="font-semibold text-slate-900 mb-2">Garantie légale de conformité</h4>
                        <p className="text-sm text-slate-600">
                          Articles L.217-3 à L.217-20 du Code de la consommation. 
                          Vous disposez d&apos;un délai de <strong>2 ans</strong> à compter de la délivrance du bien 
                          pour agir en cas de défaut de conformité.
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-50">
                      <CardContent className="p-4">
                        <h4 className="font-semibold text-slate-900 mb-2">Garantie des vices cachés</h4>
                        <p className="text-sm text-slate-600">
                          Articles 1641 à 1649 du Code civil. 
                          Vous pouvez demander le remboursement ou une réduction du prix 
                          en cas de vice caché rendant le bien impropre à l&apos;usage.
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-3 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-emerald-600" />
                    En cas de défaut de conformité, vous pouvez
                  </h3>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500 mt-1 flex-shrink-0" />
                      Demander la réparation ou le remplacement du bien
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500 mt-1 flex-shrink-0" />
                      Obtenir une réduction du prix ou la résolution de la vente si la réparation/remplacement est impossible
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500 mt-1 flex-shrink-0" />
                      Être dispensé de prouver l&apos;existence du défaut pendant les 24 premiers mois (présomption d&apos;antériorité)
                    </li>
                  </ul>
                </div>

                <div className="bg-slate-100 rounded-xl p-4">
                  <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                    <Info className="h-5 w-5 text-blue-600" />
                    Matériel d&apos;occasion ou reconditionné
                  </h4>
                  <p className="text-sm text-slate-600">
                    Pour les produits d&apos;occasion ou reconditionnés, la garantie légale de conformité 
                    est réduite à <strong>12 mois minimum</strong> conformément à la réglementation. 
                    Les éventuels défauts apparents sont signalés au moment de la vente.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Conditions Générales */}
            <Card className="border-2 border-slate-200">
              <CardHeader className="bg-slate-50">
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <div className="w-12 h-12 rounded-xl bg-slate-700 text-white flex items-center justify-center">
                    <FileText className="h-6 w-6" />
                  </div>
                  Conditions Générales d&apos;Application
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-3">
                    Mise en œuvre de la garantie
                  </h3>
                  <ul className="space-y-3 text-slate-600">
                    <li className="flex items-start gap-2">
                      <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                      Présentez-vous en boutique avec l&apos;appareil et votre facture/ticket de caisse
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                      Notre équipe effectuera un diagnostic pour déterminer l&apos;origine de la panne
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                      Si le défaut est couvert par la garantie, la réparation sera effectuée sans frais
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
                      Si le défaut n&apos;est pas couvert, un devis vous sera proposé avant toute intervention
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-3">
                    Responsabilité et limitations
                  </h3>
                  <ul className="space-y-2 text-slate-600 text-sm">
                    <li className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      Mediastore ne peut être tenu responsable de la perte de données. 
                      Il appartient au client d&apos;effectuer une sauvegarde préalable.
                    </li>
                    <li className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      La garantie ne couvre pas les accessoires (coques, chargeurs, etc.) 
                      sauf défaut de fabrication avéré.
                    </li>
                    <li className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      Tout appareil non réclamé dans un délai de 3 mois après notification 
                      sera considéré comme abandonné.
                    </li>
                    <li className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      Les délais de réparation sont donnés à titre indicatif et peuvent varier 
                      selon la disponibilité des pièces.
                    </li>
                  </ul>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Conservation des documents</h4>
                  <p className="text-sm text-slate-600">
                    Conservez précieusement votre facture ou ticket de caisse. 
                    Ce document est <strong>indispensable</strong> pour faire valoir vos droits à la garantie. 
                    Il constitue la preuve de la date d&apos;achat ou de réparation et des prestations effectuées.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Textes de loi */}
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Scale className="h-5 w-5 text-slate-600" />
                  Références légales
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-600 space-y-4">
                <div>
                  <p className="font-medium text-slate-900">Article L.217-4 du Code de la consommation :</p>
                  <p className="italic">
                    &quot;Le vendeur livre un bien conforme au contrat ainsi qu&apos;aux critères énoncés à l&apos;article L.217-5. 
                    Le vendeur répond des défauts de conformité existant au moment de la délivrance du bien [...] 
                    ainsi que des défauts de conformité résultant de l&apos;emballage, des instructions de montage 
                    ou de l&apos;installation lorsque celle-ci a été mise à sa charge par le contrat.&quot;
                  </p>
                </div>
                <div>
                  <p className="font-medium text-slate-900">Article L.217-12 du Code de la consommation :</p>
                  <p className="italic">
                    &quot;L&apos;action résultant du défaut de conformité se prescrit par deux ans 
                    à compter de la délivrance du bien.&quot;
                  </p>
                </div>
                <div>
                  <p className="font-medium text-slate-900">Article 1641 du Code civil :</p>
                  <p className="italic">
                    &quot;Le vendeur est tenu de la garantie à raison des défauts cachés de la chose vendue 
                    qui la rendent impropre à l&apos;usage auquel on la destine, ou qui diminuent tellement 
                    cet usage que l&apos;acheteur ne l&apos;aurait pas acquise, ou n&apos;en aurait donné qu&apos;un moindre prix, 
                    s&apos;il les avait connus.&quot;
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* CTA */}
            <div className="text-center py-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Une question sur nos garanties ?
              </h3>
              <p className="text-slate-600 mb-6">
                Notre équipe est à votre disposition pour vous renseigner.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={BUSINESS_PHONE_LINK}>
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 gap-2">
                    <Phone className="h-5 w-5" />
                    05 49 05 74 90
                  </Button>
                </a>
                <Link to="/contact">
                  <Button size="lg" variant="outline">
                    Nous contacter
                  </Button>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
